import {LLMToolResultBlock, LLMToolCallBlock} from 'llm/chat-library/messages/MessageTypes';
import {ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {DeepResearchProgressToolCall} from 'llm/deep-research/tool-details/types';

import {
  DeepResearchToolType,
  SearchToolCall,
  GetWarehouseOverviewToolCall,
  QueryWarehouseToolCall,
  ReadBenchlingDocumentToolCall,
  SearchNotebookEntriesToolCall,
  SearchSequencesToolCall,
  AttachmentToolCall,
  PublicDataSubAgentToolCall,
  IdsToModelsToolCall,
  PlottingToolCall,
  MCPToolCall,
  isMCPTool,
  parseMCPToolName,
  GenerateImageToolCall,
  ThinkToolCall,
  ResearchSubAgentToolCall,
  CompleteAnswerToolCall,
  BaseDeepResearchProgressToolCall,
  TOOL_DESCRIPTIONS_IN_PROGRESS,
  TOOL_DESCRIPTIONS_COMPLETED,
} from './types';

// Utility function to extract text content from tool result
function extractResultText(toolResult: LLMToolResultBlock): string {
  return toolResult.value.content
    .filter((content) => content.type === 'text')
    .map((content) => {
      const textBlock = content;
      if (textBlock.value.text) {
        return textBlock.value.text;
      }
      return '';
    })
    .join(' ')
    .trim();
}

// Helper to get display name for a tool
function getToolDisplayName(toolName: string, status: ToolCallStatus): string {
  if (isMCPTool(toolName)) {
    return status === ToolCallStatus.COMPLETED ? 'Used MCP tool' : 'Using MCP tool';
  }

  const descriptions =
    status === ToolCallStatus.COMPLETED ? TOOL_DESCRIPTIONS_COMPLETED : TOOL_DESCRIPTIONS_IN_PROGRESS;
  const description = descriptions[toolName as DeepResearchToolType];
  if (description) {
    return description;
  }

  // For unknown tools, use a generic description
  return status === ToolCallStatus.COMPLETED ? 'Worked on your request' : 'Working on your request';
}

// Base function to create common tool call properties
function createBaseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): BaseDeepResearchProgressToolCall {
  const resultText = toolResultBlock ? extractResultText(toolResultBlock) : undefined;
  const status = toolResultBlock ? ToolCallStatus.COMPLETED : ToolCallStatus.PENDING;
  const toolName = toolCallBlock.value.name;

  return {
    tool: toolName,
    toolId: toolCallBlock.value.id,
    createdAt,
    status,
    displayName: getToolDisplayName(toolName, status),
    responseText: resultText,
    args: toolCallBlock.value.args,
  };
}

// Individual tool call parsers
function parseSearchToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchTool,
    query: (toolCallBlock.value.args.query as string) || '',
    objectTypes: (toolCallBlock.value.args.objectTypes as Array<string>) || [],
  };
}

function parseSearchNotebookEntriesToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchNotebookEntriesToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchNotebookEntries,
    queries: (toolCallBlock.value.args.search_queries as Array<string>) || [],
    authoredBy: toolCallBlock.value.args.authored_by as Array<string> | undefined,
    createdAfter: toolCallBlock.value.args.created_after as string | undefined,
    createdBefore: toolCallBlock.value.args.created_before as string | undefined,
    modifiedAfter: toolCallBlock.value.args.modified_after as string | undefined,
    modifiedBefore: toolCallBlock.value.args.modified_before as string | undefined,
    reviewCompletedAfter: toolCallBlock.value.args.review_completed_after as string | undefined,
    reviewCompletedBefore: toolCallBlock.value.args.review_completed_before as string | undefined,
    reviewStatus: toolCallBlock.value.args.review_status as string | undefined,
    reviewRole: toolCallBlock.value.args.review_role as string | undefined,
    inProjectOrFolder: toolCallBlock.value.args.in_project_or_folder as string | undefined,
    inStudy: toolCallBlock.value.args.in_study as string | undefined,
    offset: (toolCallBlock.value.args.offset as number) || 0,
  };
}

function parseSearchSequencesToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): SearchSequencesToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.SearchSequencesTool,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseGetWarehouseOverviewToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): GetWarehouseOverviewToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.GetWarehouseOverviewTool,
  };
}

function parseQueryWarehouseToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): QueryWarehouseToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.QueryWarehouseTool,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseNotebookQueryToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ReadBenchlingDocumentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.ReadBenchlingDocumentTool,
    documentId: (toolCallBlock.value.args.document_id as string) || '',
  };
}

function parseAttachmentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): AttachmentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.AttachmentTool,
    attachmentId: (toolCallBlock.value.args.attachmentId as string) || '',
  };
}

function parsePublicDataSubAgentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): PublicDataSubAgentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.PublicDataSubAgent,
    query: (toolCallBlock.value.args.query as string) || '',
  };
}

function parseIdsToModelsToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): IdsToModelsToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.IdsToModelsTool,
    objectIds: (toolCallBlock.value.args.object_ids as Array<string>) || [],
    includeRelevantItems: (toolCallBlock.value.args.include_relevant_items as boolean) || false,
  };
}

function parsePlottingToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): PlottingToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.PlottingTool,
    code: (toolCallBlock.value.args.code as string) || '',
  };
}

function parseGenerateImageToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): GenerateImageToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    tool: DeepResearchToolType.GenerateImageTool,
    prompt: (toolCallBlock.value.args.prompt as string) || '',
  };
}

function parseThinkToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ThinkToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);
  return {
    ...baseCall,
    tool: DeepResearchToolType.Think,
  };
}

function parseResearchSubAgentToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ResearchSubAgentToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);
  return {
    ...baseCall,
    tool: DeepResearchToolType.ResearchSubAgent,
  };
}

function parseCompleteAnswerToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): CompleteAnswerToolCall {
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);
  return {
    ...baseCall,
    tool: DeepResearchToolType.CompleteAnswer,
  };
}

// MCP tool parser
function parseMCPToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): MCPToolCall {
  const toolName = toolCallBlock.value.name;
  const {serverName, originalToolName} = parseMCPToolName(toolName);
  const baseCall = createBaseToolCall(toolCallBlock, toolResultBlock, createdAt);

  return {
    ...baseCall,
    // Cast is safe: this function is only called when isMCPTool(toolName) is true
    tool: toolName as MCPToolCall['tool'],
    serverName,
    originalToolName,
    args: toolCallBlock.value.args,
  };
}

// Tool parser registry
const toolParsers: Record<
  DeepResearchToolType,
  (
    toolCallBlock: LLMToolCallBlock,
    toolResultBlock: LLMToolResultBlock | null,
    createdAt: Date
  ) => DeepResearchProgressToolCall | null
> = {
  [DeepResearchToolType.SearchTool]: parseSearchToolCall,
  [DeepResearchToolType.SearchNotebookEntries]: parseSearchNotebookEntriesToolCall,
  [DeepResearchToolType.SearchSequencesTool]: parseSearchSequencesToolCall,
  [DeepResearchToolType.GetWarehouseOverviewTool]: parseGetWarehouseOverviewToolCall,
  [DeepResearchToolType.QueryWarehouseTool]: parseQueryWarehouseToolCall,
  [DeepResearchToolType.ReadBenchlingDocumentTool]: parseNotebookQueryToolCall,
  [DeepResearchToolType.AttachmentTool]: parseAttachmentToolCall,
  [DeepResearchToolType.ResearchSubAgent]: parseResearchSubAgentToolCall,
  [DeepResearchToolType.Think]: parseThinkToolCall,
  [DeepResearchToolType.CompleteAnswer]: parseCompleteAnswerToolCall,
  [DeepResearchToolType.PublicDataSubAgent]: parsePublicDataSubAgentToolCall,
  [DeepResearchToolType.IdsToModelsTool]: parseIdsToModelsToolCall,
  [DeepResearchToolType.PlottingTool]: parsePlottingToolCall,
  [DeepResearchToolType.GenerateImageTool]: parseGenerateImageToolCall,
};

/**
 * Main function to convert a tool call and optional result into a DeepResearchProgressToolCall
 */
export function getToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): DeepResearchProgressToolCall | null {
  const toolName = toolCallBlock.value.name;

  // Check if this is an MCP tool (contains server name separator)
  if (typeof toolName === 'string' && isMCPTool(toolName)) {
    return parseMCPToolCall(toolCallBlock, toolResultBlock, createdAt);
  }

  // Look up parser for known DeepResearchToolType
  const parser = toolParsers[toolName as DeepResearchToolType];

  if (!parser) {
    return null;
  }

  return parser(toolCallBlock, toolResultBlock, createdAt);
}
